import React from 'react';
import { NavLink, Outlet, Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { FiGrid, FiBox, FiMaximize2, FiGift, FiUsers, FiShoppingCart, FiSettings, FiLogOut } from 'react-icons/fi';

const AdminLayout: React.FC = () => {
  const { user, logout, isAuthenticated } = useAuth();
  const location = useLocation();

  if (!isAuthenticated) {
    return <Navigate to="/console/login" state={{ from: location }} replace />;
  }

  const navItems = [
    { to: '/console/dashboard', label: 'Dashboard', icon: <FiGrid />, section: 'Overview' },
    { to: '/console/products', label: 'Products', icon: <FiBox />, section: 'Manage' },
    { to: '/console/qr-codes', label: 'QR Codes', icon: <FiMaximize2 />, section: 'Manage' },
    { to: '/console/schemes', label: 'Schemes', icon: <FiGift />, section: 'Manage' },
    { to: '/console/users', label: 'Users', icon: <FiUsers />, section: 'Manage' },
    { to: '/console/orders', label: 'Orders', icon: <FiShoppingCart />, section: 'Manage' },
    { to: '/console/config', label: 'Settings', icon: <FiSettings />, section: 'System' },
  ];

  const getPageTitle = () => {
    const path = location.pathname.split('/').pop();
    const titles: Record<string, string> = {
      dashboard: 'Dashboard',
      products: 'Products',
      'qr-codes': 'QR Codes',
      schemes: 'Schemes',
      users: 'Users',
      orders: 'Orders',
      config: 'Settings',
    };
    return titles[path || ''] || 'Dashboard';
  };

  const getPageDesc = () => {
    const path = location.pathname.split('/').pop();
    const desc: Record<string, string> = {
      dashboard: 'Overview of your loyalty program',
      products: 'Manage products and reward mappings',
      'qr-codes': 'Generate and track QR batches',
      schemes: 'Manage reward schemes and catalogs',
      users: 'View and manage all users',
      orders: 'Track redemption orders',
      config: 'System configuration',
    };
    return desc[path || ''] || '';
  };

  const userInitial = user?.fullName?.charAt(0)?.toUpperCase() || 'A';

  let lastSection = '';
  const renderNav = () => {
    return navItems.map((item) => {
      const showLabel = item.section !== lastSection;
      lastSection = item.section;
      return (
        <React.Fragment key={item.to}>
          {showLabel && <div className="sidebar-section-label">{item.section}</div>}
          <NavLink
            to={item.to}
            className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}
          >
            {item.icon}
            {item.label}
          </NavLink>
        </React.Fragment>
      );
    });
  };

  return (
    <div className="admin-layout">
      <aside className="sidebar">
        <div className="sidebar-logo">
          <img src="/logo.png" alt="Lancer Loyalty" />
          <div className="sidebar-logo-text">
            LANCER
            <span>Loyalty Console</span>
          </div>
        </div>
        <nav className="sidebar-nav">
          {renderNav()}
        </nav>
        <div className="sidebar-footer">
          <div className="sidebar-user">
            <div className="sidebar-user-avatar">{userInitial}</div>
            <div className="sidebar-user-info">
              <strong>{user?.fullName}</strong>
              <span>{user?.role?.replace('_', ' ')}</span>
            </div>
          </div>
          <button className="btn btn-outline" style={{ width: '100%', color: '#8899a6', borderColor: 'rgba(255,255,255,0.1)', background: 'rgba(255,255,255,0.03)', fontSize: 12.5 }} onClick={logout}>
            <FiLogOut /> Sign Out
          </button>
        </div>
      </aside>

      <main className="main-content">
        <header className="topbar">
          <div>
            <h2>{getPageTitle()}</h2>
            <p style={{ fontSize: 12.5, color: '#9ca3af', marginTop: 1, fontWeight: 400 }}>{getPageDesc()}</p>
          </div>
          <div className="topbar-right">
            <div className="topbar-badge">
              <span className="dot"></span>
              API Connected
            </div>
          </div>
        </header>
        <div className="page-content">
          <Outlet />
        </div>
      </main>
    </div>
  );
};

export default AdminLayout;
